import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import RoundedButton from './commons/RoundedButton';
import arrowLeft from '../images/arrow-left.svg';
import { FormationColumnLessons } from './FormationSingleColumn';
import { FormationType, VersionItem, VersionAccount } from '../types';
import ObjectId from 'bson-objectid';
import { useApiService } from '@/hooks/useApiService';
import { useTranslation } from 'react-i18next';

function Formation() {
    const { getVersionsAccountStatuses, getFormation } = useApiService();
    const formation_id = useParams().formationId || '';
    const [versionFormation, setFormation] = useState<FormationType>();
    const [viewStep, setViewStep] = useState('intro');

    const [versionsAccountStatuses, setVersionsAccountStatuses] = useState<VersionAccount[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            const accountVersions = (await getVersionsAccountStatuses()) as VersionAccount[];

            setVersionsAccountStatuses(accountVersions);
        };

        fetchData().catch(console.error);
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            const versionFormation = await getFormation(formation_id);
            setFormation(versionFormation);
        };

        fetchData().catch(console.error);
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formation_id]);

    return (
        <div className="z-10 relative w-auto min-h-[100vh] m-auto flex flex-col items-center overflow-hidden ">
            <img
                className="pointer-events-none absolute z-10 top-20 m-0 min-w-auto  max-h-0 lg:top-40 3xl:-top-[200px] 3xl:-right-[980px]"
                src={`/images/deco/formation-ar-${versionFormation?.theme || 'indigo'}.svg`}
                alt="Course line"
            />
            <img
                className="pointer-events-none absolute z-10 top-40 min-w-auto rotate-12 hidden 3xl:block 3xl:top-auto 3xl:-bottom-[1100px] 3xl:-left-[1500px]"
                src={`/images/deco/formation-ar-${versionFormation?.theme || 'indigo'}.svg`}
                alt="Course line"
            />
            <div
                className={`absolute z-0 w-full h-[700px] bg-gradient-to-b from-ar-${versionFormation?.theme}/10 to-white`}
            ></div>
            <div className="z-20 pt-24 relative flex flex-col w-[900px] max-w-[90%] mx-auto">
                {versionFormation && viewStep === 'intro' ? (
                    <FormationIntro formation={versionFormation} setViewStep={setViewStep} />
                ) : (
                    versionFormation && (
                        <FormationVersionList
                            formation={versionFormation}
                            setViewStep={setViewStep}
                            versionsAccountStatuses={versionsAccountStatuses}
                        />
                    )
                )}
                <div className="mt-32"></div>
            </div>
        </div>
    );
}

function FormationIntro({
    formation,
    setViewStep,
}: {
    formation: FormationType;
    setViewStep: (value: string) => void;
}) {
    const { t } = useTranslation();
    return (
        <section>
            <div className="flex flex-col justify-between items-start md:flex-row">
                <div className="flex flex-col">
                    <h1 className="text-text-dark font-semibold text-5xl -tracking-[.08em] -ml-0.5">
                        {t(formation?.name)}
                    </h1>
                    <p className="mt-2 text-text-dark font-semibold text-sm">
                        {t('school_duration')} · {formation.totalDuration} {t('school_minutes')}
                    </p>
                    <p className="mt-4 text-md sm:text-md font-medium text-text-grey">{t(formation?.description)}</p>
                </div>

                <RoundedButton
                    theme={formation.theme}
                    label={t('Global_Next')}
                    disabled={true}
                    onClick={() => {
                        setViewStep('versions');
                    }}
                />
            </div>
            <div className="relative mt-6">
                <iframe
                    className="relative z-10 aspect-video w-full rounded-xl  border	outline-gray-100 border-solid"
                    src={formation.intro_video_url}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Embedded youtube"
                />
                <div
                    className={`absolute flex items-center justify-center z-20 top-0 left-0 w-fit rounded-tl-xl rounded-br-xl bg-ar-${formation.theme} h-[32px] px-3`}
                >
                    <span className=" tracking-widest text-white font-semibold text-[13px]">
                        {t('school_introduction')}
                    </span>
                </div>
            </div>
            <div className="mt-8 w-full border-t border-[#E0E3E9]"></div>
            <div className="mt-6 flex flex-col w-full">
                <h3 className="text-lg font-semibold text-text-dark">{t('school_formation_content')}</h3>
                <FormationColumnLessons versionFormation={formation} />
            </div>
            <div className="mt-8 w-full border-t border-[#E0E3E9]"></div>
            <div className="mt-6 flex w-full flex-col gap-6 lg:flex-row">
                <div className="flex flex-col w-full">
                    <h3 className="text-lg font-semibold text-text-dark mb-2">{t('school_receive')} ...</h3>
                    <div className="flex flex-row items-center gap-3">
                        <img
                            className="pointer-events-none w-[150px] select-none shadow-sm border border-[#F4F4F4] rounded-[5px]"
                            src={`/images/badge-${formation.theme}.svg`}
                            alt="Archireport certified badge"
                        />
                        <img
                            className="pointer-events-none w-[150px] select-none shadow-sm border border-[#F4F4F4] rounded-[5px]"
                            src={`/images/pdf-${formation.theme}.svg`}
                            alt="Certificat PDF"
                        />
                    </div>
                </div>
                <div className="flex flex-col w-full">
                    <h3 className="text-lg font-semibold text-text-dark">{t('school_available_platforms')}</h3>
                    <p className="text-md mt-2 font-medium text-text-grey">{t(formation?.platforms_label)}</p>
                </div>
            </div>
        </section>
    );
}

function FormationVersionList({
    formation,
    setViewStep,
    versionsAccountStatuses,
}: {
    formation: FormationType;
    setViewStep: (value: string) => void;
    versionsAccountStatuses: VersionAccount[];
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [selectedVersionId, setSelectedVersionId] = useState<string | ObjectId | undefined>();
    const [firstFalseVersionLesson, setFirstFalseVersionLesson] = useState<VersionItem | number>();

    const calculateVersionProgress = (version: VersionItem) => {
        const versionStatus = versionsAccountStatuses.find(status => status.formation_version_id === version._id);

        const totalLessons = version.lessons?.length || 0;
        const finishedLessons = versionStatus?.lesson_statuses
            ? Object.values(versionStatus.lesson_statuses).filter(Boolean).length
            : 0;

        const isCompleted = finishedLessons === totalLessons && totalLessons > 0;

        return { totalLessons, finishedLessons, isCompleted, versionStatus };
    };

    return (
        <section>
            <div
                className="flex items-center rounded-full mb-6 justify-center w-12 h-12 bg-white/70 backdrop-blur-lg border border-[#EAECF0] shadow-sm hover:shadow-md hover:cursor-pointer active:shadow-none active:bg-gray-50"
                onClick={() => {
                    setViewStep('intro');
                }}
            >
                <img src={arrowLeft} alt="Back arrow" />
            </div>
            <div className="flex flex-col justify-between items-start md:flex-row">
                <div className="flex flex-col max-w-[70%]">
                    <h1 className="text-text-dark font-semibold text-2xl">{t('school_how_use_archireport')}</h1>
                    <p className="mt-3 text-md sm:text-md font-medium text-text-grey">
                        {t('school_please_select_how_you_will_use_archireport')}
                    </p>
                </div>
                {selectedVersionId ? (
                    <RoundedButton
                        theme={formation.theme}
                        label={(() => {
                            const versionStatus = versionsAccountStatuses.find(
                                version => version.formation_version_id === selectedVersionId
                            );
                            return versionStatus ? t('school_continue_formation') : t('school_begin');
                        })()}
                        disabled={!selectedVersionId}
                        onClick={() => {
                            if (!selectedVersionId) return;
                            navigate(
                                `/formation/${formation._id}/version/${selectedVersionId}/lesson/${firstFalseVersionLesson}`
                            );
                        }}
                    />
                ) : (
                    <div></div>
                )}
            </div>
            <div className="w-full mt-8 flex flex-col rounded-xl border border-[#E4E4EA] bg-white/70 backdrop-blur-xl divide-y divide-solid divide-[#E4E4EA] shadow-lg">
                {formation?.formationVersions ? (
                    formation.formationVersions.map(function (version, index) {
                        const { totalLessons, finishedLessons, isCompleted, versionStatus } =
                            calculateVersionProgress(version);
                        return (
                            <div
                                key={index}
                                className="relative flex flex-col-reverse pt-5 cursor-pointer	lg:pt-7 pl-5 gap-6 lg:pl-7 lg:gap-14 md:flex-row hover:bg-gray-50"
                                onClick={() => {
                                    const element = document.getElementById(
                                        `bordered-radio-${index}`
                                    ) as HTMLInputElement;
                                    if (element) {
                                        element.checked = true;
                                    }
                                    setSelectedVersionId(version._id);

                                    isCompleted
                                        ? setFirstFalseVersionLesson(1)
                                        : setFirstFalseVersionLesson(finishedLessons + 1);
                                }}
                            >
                                <img
                                    className="w-[300px] h-[156px] -ml-4 lg:ml-0"
                                    loading="lazy"
                                    src={version.image.includes('http') ? version.image : `/images/${version.image}`}
                                    alt={`${version.platforms.join(', ')} version`}
                                />
                                <div className="flex flex-col">
                                    <div className="flex flex-row items-center gap-2 lg:mt-2">
                                        <h2
                                            className="font-semibold text-lg text-text-dark max-w-[80%] sm:max-w-[100%]"
                                            dangerouslySetInnerHTML={{ __html: `${t(version.name)}` }}
                                        ></h2>
                                        {version.platforms.map(platform => {
                                            return (
                                                <img
                                                    key={platform}
                                                    alt={platform}
                                                    src={`/images/${platform}-icon.svg`}
                                                    className="hidden sm:block"
                                                />
                                            );
                                        })}
                                    </div>
                                    <p className="mt-2 max-w-[330px] text-md leading-5 font-medium text-text-grey">
                                        {t(version.description)}
                                    </p>
                                </div>

                                <input
                                    id={`bordered-radio-${index}`}
                                    type="radio"
                                    value={version._id.toString()}
                                    name="bordered-radio"
                                    onClick={() => {
                                        setSelectedVersionId(version._id.toString());
                                    }}
                                    className={`appearance-none absolute top-4 right-4 text-ar-${formation.theme} lg:top-6 lg:right-6 w-6 h-6 bg-gray-100 border-gray-300 focus:border-ar-${formation.theme} focus:ring-ar-${formation.theme} focus:bg-ar-${formation.theme} checked:bg-ar-${formation.theme} active:bg-ar-${formation.theme} hover:bg-ar-${formation.theme}`}
                                ></input>

                                {versionStatus && (
                                    <div
                                        className={`flex absolute bottom-4 right-4 items-center justify-center py-1 px-2.5 rounded-full text-xs font-semibold bg-ar-${formation.theme} text-white `}
                                    >
                                        <p>
                                            {isCompleted
                                                ? t('school_finished_formation')
                                                : t('school_number_of_finished_lessons', {
                                                      nbFinished: finishedLessons,
                                                      nbTotal: totalLessons,
                                                  })}
                                        </p>
                                    </div>
                                )}
                            </div>
                        );
                    })
                ) : (
                    <div>Aucune version</div>
                )}
            </div>
        </section>
    );
}

export default Formation;
